import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/reducers';
import { EduCapabilities } from './types';
import { emptyEduCapabilities } from './capabilitiesModel';
import { eduErrorMessage, getEduCapabilities } from './api';
import { subscribeEduAccessChange } from './accessEvents';

type EduCapabilityState = { capabilities: EduCapabilities; ready: boolean; error: string; refresh: () => Promise<void> };
const Context = createContext<EduCapabilityState>({ capabilities: emptyEduCapabilities(), ready: false, error: '', refresh: async () => {} });
export function useEduCapabilities() { return useContext(Context); }

export function EduCapabilitiesProvider({ children }: { children: React.ReactNode }) {
    const auth = useSelector((state: RootState) => state.auth);
    const identity = JSON.stringify([auth.id, auth.userId]);
    const currentIdentity = useRef(identity); currentIdentity.current = identity;
    const request = useRef(0); const controller = useRef<AbortController | null>(null);
    const [state, setState] = useState({ identity: '', capabilities: emptyEduCapabilities(), ready: false, error: '' });
    const refresh = useCallback(async (clear = false) => {
        const sequence = ++request.current; controller.current?.abort();
        const next = new AbortController(); controller.current = next;
        if (clear) setState({ identity, capabilities: emptyEduCapabilities(), ready: false, error: '' });
        if (!auth.id) { setState({ identity, capabilities: emptyEduCapabilities(), ready: true, error: '' }); return; }
        try {
            const capabilities = await getEduCapabilities(next.signal);
            if (!next.signal.aborted && sequence === request.current && currentIdentity.current === identity)
                setState({ identity, capabilities, ready: true, error: capabilities.accessVersion ? '' : 'Não foi possível confirmar o acesso Fermi Edu.' });
        } catch (cause) {
            if (!next.signal.aborted && sequence === request.current && currentIdentity.current === identity)
                setState({ identity, capabilities: emptyEduCapabilities(), ready: true, error: eduErrorMessage(cause) });
        }
    }, [identity, auth.id]);
    const invalidate = useCallback(() => { ++request.current; controller.current?.abort(); }, []);
    useEffect(() => {
        refresh(true);
        return invalidate;
    }, [refresh, invalidate]);
    useEffect(() => {
        const focus = () => { void refresh(); };
        const visible = () => { if (document.visibilityState === 'visible') focus(); };
        window.addEventListener('focus', focus); document.addEventListener('visibilitychange', visible);
        const unsubscribe = subscribeEduAccessChange(change => {
            if (change.token && change.token !== String(auth.id)) return;
            void refresh(true);
        });
        return () => { window.removeEventListener('focus', focus); document.removeEventListener('visibilitychange', visible); unsubscribe(); };
    }, [refresh, identity, auth.id]);
    const scoped = state.identity === identity ? state : { capabilities: emptyEduCapabilities(), ready: false, error: '' };
    return <Context.Provider value={{ capabilities: scoped.capabilities, ready: scoped.ready, error: scoped.error, refresh: () => refresh() }}>{children}</Context.Provider>;
}
